import { pick, serverMap } from '@/shared/lib/server-map'
import type { IconName } from '@/shared/ui/icon'
import type { TimelineEvent, TimelineMessage } from '@/shared/api'

// Выведены из контракта, а не переписаны литералом: рукописная копия молча не заметила бы значения,
// добавленного на бекенде.
type EventTone = NonNullable<TimelineEvent['tone']>
type DeliveryStatus = NonNullable<TimelineMessage['deliveryStatus']>

/**
 * Тон события → CSS-токен: им красится плитка события и обе заливки карточки.
 *
 * ⚠️ СОЗНАТЕЛЬНОЕ ОТСТУПЛЕНИЕ ОТ МАКЕТА, НЕ ЧИНИТЬ. Там единственное событие («Бронь подтверждена»)
 * окрашено цветом НОВОГО СТАТУСА БРОНИ, а повторить это нечем: статуса в элементе ленты контракт не
 * отдаёт, он отдаёт семантический `tone` — «Сервер про палитру не знает» (`TimelineEvent.tone`). На
 * экране видно: «Бронь подтверждена» зелёная, а не синяя.
 *
 * Ключ карты — `string`: незнакомый тон уезжает в нейтральный фолбэк, а не роняет ленту (правило —
 * в `entities/booking/model/server-drift.ts`).
 */
const TONE_TOKENS = serverMap({
  success: 'var(--st-in)',
  warning: 'var(--st-pending)',
  danger: 'var(--st-cancel)',
  neutral: 'var(--ink-3)',
})

const TONE_FALLBACK = 'var(--ink-3)'

/** `tone` в контракте необязателен — событие без тона тоже нейтральное. */
export function eventToneToken(tone: EventTone | null | undefined): string {
  if (!tone) return TONE_FALLBACK
  return pick(TONE_TOKENS, tone, TONE_FALLBACK)
}

/**
 * Тон события → иконка на плитке.
 *
 * ⚠️ В МАКЕТЕ ИКОНКА ОДНА — ГАЛКА, потому что и событие там одно. Скопировать буквально означало бы
 * поставить галку на «Бронь отменена», а галка утверждает «всё хорошо, готово». Поэтому иконка
 * выбирается из того же `tone`, что и цвет: сервер отдаёт СМЫСЛ, фронт решает, как его показать.
 * Нейтральный тон получает точку — нейтральное событие ничего не утверждает, оно просто произошло.
 */
const TONE_ICONS = serverMap<IconName>({
  success: 'check',
  warning: 'alert',
  danger: 'close',
  neutral: 'dot',
})

const TONE_ICON_FALLBACK: IconName = 'dot'

export function eventToneIcon(tone: EventTone | null | undefined): IconName {
  if (!tone) return TONE_ICON_FALLBACK
  return pick(TONE_ICONS, tone, TONE_ICON_FALLBACK)
}

/**
 * Статус доставки → галочки. Это ЛИТЕРАЛЬНЫЙ ТЕКСТ в строке времени (`13:52 ✓✓`), а не SVG — так
 * нарисовано в макете. Само соответствие подтверждает контракт (`conversation.yaml`, `sent → ✓`,
 * `read → ✓✓`); про способ отрисовки он не говорит ничего.
 *
 * ⚠️ ВСЁ ОСТАЛЬНОЕ — БЕЗ ГАЛОЧКИ, и это главное свойство карты: галочка утверждает «доставлено», а
 * для `sending`, `failed` и незнакомого значения такого утверждения у нас нет. Оба состояния
 * показывает вид самого пузыря (`ui/MessageBubble`): приглушён и «Не отправлено» под ним.
 */
const DELIVERY_MARKS = serverMap({
  sent: '✓',
  read: '✓✓',
})

/** `deliveryStatus` есть только у исходящих — у входящих метки нет. */
export function deliveryMark(status: DeliveryStatus | null | undefined): string | null {
  if (!status) return null
  return pick(DELIVERY_MARKS, status, null)
}
